import { Injectable } from '@angular/core';
import { environment } from '../../../environments';

// Type definitions - types are declared in firebase-analytics.d.ts
// This allows compilation even if @angular/fire/analytics is not installed
type Analytics = any;
type LogEventFn = (analytics: Analytics, eventName: string, eventParams?: { [key: string]: any }) => void;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private analytics: Analytics | null = null;
  private isEnabled = false;
  private logEventFn: LogEventFn | null = null;
  private packageAvailable = false;

  constructor() {
    // Vérifier si Analytics est activé dans l'environnement
    this.isEnabled = environment.features?.enableAnalytics ?? false;
    
    if (this.isEnabled) {
      // Note: Firebase Analytics will only work if:
      // 1. @angular/fire/analytics package is installed
      // 2. Firebase Analytics is properly configured in app.config.ts
      // 3. Analytics token is provided via Angular DI
      
      // For now, the service will work in disabled mode
      // To enable Firebase Analytics, install the package and configure it properly
      console.warn('[Analytics] Firebase Analytics nécessite l\'installation de @angular/fire/analytics et une configuration appropriée');
      console.log('[Analytics] Service fonctionne en mode désactivé - les événements seront loggés mais non envoyés à Firebase');
    } else {
      console.log('[Analytics] Service désactivé dans l\'environnement');
    }
  }

  /**
   * Log un événement Firebase Analytics avec des logs détaillés
   * @param eventName Nom de l'événement
   * @param eventParams Paramètres de l'événement (optionnel)
   */
  logEvent(eventName: string, eventParams?: { [key: string]: any }): void {
    const timestamp = new Date().toISOString();
    
    // Log avant l'envoi
    console.log(`[Analytics] 📊 Tentative d'envoi d'événement:`, {
      timestamp,
      eventName,
      eventParams: eventParams || {},
      enabled: this.isEnabled,
      analyticsAvailable: !!this.analytics,
    });

    // Vérifier si Analytics est activé
    if (!this.isEnabled) {
      console.warn(`[Analytics] ⚠️ Événement "${eventName}" ignoré - Analytics désactivé dans l'environnement`);
      return;
    }

    // Vérifier si Analytics est disponible
    if (!this.analytics || !this.logEventFn) {
      // Service fonctionne en mode no-op - log l'événement mais ne l'envoie pas
      console.log(`[Analytics] 📝 Événement "${eventName}" loggé (Firebase Analytics non disponible)`, {
        timestamp,
        eventName,
        eventParams: eventParams || {},
      });
      return;
    }

    try {
      // Envoyer l'événement
      this.logEventFn(this.analytics, eventName, eventParams);
      
      // Log après l'envoi réussi
      console.log(`[Analytics] ✅ Événement "${eventName}" envoyé avec succès`, {
        timestamp,
        eventName,
        eventParams: eventParams || {},
      });
    } catch (error) {
      // Log en cas d'erreur
      console.error(`[Analytics] ❌ Erreur lors de l'envoi de l'événement "${eventName}":`, {
        error,
        eventName,
        eventParams: eventParams || {},
        timestamp,
      });
    }
  }

  /**
   * Vérifie si le service Analytics est disponible et activé
   */
  isAvailable(): boolean {
    return this.isEnabled && !!this.analytics;
  }

  /**
   * Log les informations de statut du service
   */
  logStatus(): void {
    console.log('[Analytics] 📋 Statut du service:', {
      enabled: this.isEnabled,
      analyticsAvailable: !!this.analytics,
      packageAvailable: this.packageAvailable,
      environment: environment.production ? 'production' : 'development',
    });
  }
}
